import { Vector3 } from "three";

/**
 * Refine a picked point: fit a local plane through the sampled
 * neighborhood (PCA), reject far-off samples (scaled MAD), then project
 * the anchor point onto the filtered plane.
 *
 * Coordinates stay in the same space as the input samples.
 */

export const MIN_SAMPLES = 12;
export const MAD_SCALE = 1.4826; // consistency constant vs. standard deviation
export const MAD_K = 2; // reject residuals beyond K * scaled MAD

interface IPlane {
  centroid: Vector3;
  normal: Vector3;
}

const fitPlanePCA = (points: Vector3[]): IPlane | undefined => {
  const centroid = new Vector3();
  for (const p of points) centroid.add(p);
  centroid.divideScalar(points.length);

  let xx = 0,
    xy = 0,
    xz = 0,
    yy = 0,
    yz = 0,
    zz = 0;
  for (const p of points) {
    const dx = p.x - centroid.x;
    const dy = p.y - centroid.y;
    const dz = p.z - centroid.z;
    xx += dx * dx;
    xy += dx * dy;
    xz += dx * dz;
    yy += dy * dy;
    yz += dy * dz;
    zz += dz * dz;
  }

  const normal = smallestEigenvector([
    [xx, xy, xz],
    [xy, yy, yz],
    [xz, yz, zz],
  ]);
  if (!normal) return undefined;
  return { centroid, normal };
};

/**
 * Jacobi eigenvalue iteration for a symmetric 3x3 matrix.
 * Returns the unit eigenvector of the smallest eigenvalue
 * (= local surface normal when fed a covariance matrix).
 */
const smallestEigenvector = (m: number[][]): Vector3 | undefined => {
  const a = m.map((row) => row.slice());
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  for (let sweep = 0; sweep < 16; sweep++) {
    const off = Math.abs(a[0][1]) + Math.abs(a[0][2]) + Math.abs(a[1][2]);
    if (off < 1e-12) break;
    for (let p = 0; p < 2; p++) {
      for (let q = p + 1; q < 3; q++) {
        if (Math.abs(a[p][q]) < 1e-15) continue;
        const theta = (a[q][q] - a[p][p]) / (2 * a[p][q]);
        const t =
          (theta >= 0 ? 1 : -1) /
          (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < 3; k++) {
          const akp = a[k][p];
          const akq = a[k][q];
          a[k][p] = c * akp - s * akq;
          a[k][q] = s * akp + c * akq;
        }
        for (let k = 0; k < 3; k++) {
          const apk = a[p][k];
          const aqk = a[q][k];
          a[p][k] = c * apk - s * aqk;
          a[q][k] = s * apk + c * aqk;
        }
        for (let k = 0; k < 3; k++) {
          const vkp = v[k][p];
          const vkq = v[k][q];
          v[k][p] = c * vkp - s * vkq;
          v[k][q] = s * vkp + c * vkq;
        }
      }
    }
  }
  const eigenvalues = [a[0][0], a[1][1], a[2][2]];
  let minIdx = 0;
  if (eigenvalues[1] < eigenvalues[minIdx]) minIdx = 1;
  if (eigenvalues[2] < eigenvalues[minIdx]) minIdx = 2;
  const normal = new Vector3(v[0][minIdx], v[1][minIdx], v[2][minIdx]);
  return normal.lengthSq() > 0 ? normal.normalize() : undefined;
};

const median = (values: number[]): number => {
  const sorted = values.slice().sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const refinePointPCA = (
  samples: Vector3[],
  anchor: Vector3
): Vector3 => {
  if (samples.length < MIN_SAMPLES) return anchor.clone();

  const first = fitPlanePCA(samples);
  if (!first) return anchor.clone();

  // signed distance of each sample to the first-pass plane
  const residuals = samples.map((p) =>
    new Vector3().subVectors(p, first.centroid).dot(first.normal)
  );
  const med = median(residuals);
  const mad = median(residuals.map((r) => Math.abs(r - med)));

  let inliers = samples;
  if (mad > 1e-9) {
    const threshold = MAD_K * MAD_SCALE * mad;
    inliers = samples.filter(
      (_, i) => Math.abs(residuals[i] - med) <= threshold
    );
  }
  if (inliers.length < MIN_SAMPLES) return anchor.clone();

  const plane =
    inliers === samples ? first : fitPlanePCA(inliers) ?? first;

  const distance = new Vector3()
    .subVectors(anchor, plane.centroid)
    .dot(plane.normal);
  return anchor.clone().addScaledVector(plane.normal, -distance);
};
